/**
 * What kind of client made this request, from its user agent.
 *
 * WHY THIS EXISTS. "Bot" meant three different things in three files. The
 * funnel ingest dropped sixteen patterns plus every empty user agent; the event
 * ingest dropped a different sixteen, all named crawlers; the dashboard tested
 * a third set of four. A request could be a bot to one and a person to another,
 * and the dashboard counted a population that neither ingest had actually
 * excluded. None of the three recorded what it dropped, so the traffic they
 * disagreed about was gone before anyone could look at it.
 *
 * A CRAWLER IS NOT A BOT. Googlebot indexing a listing, GPTBot reading it,
 * AhrefsBot counting links and a headless Chrome scraping prices are four
 * different events with four different consequences, and lumping them under one
 * word throws away the only question worth asking: who is this, and what do
 * they want. They are separate classes here, by purpose.
 *
 * NOTHING IS DROPPED FOR BEING A BOT. This function classifies; it does not
 * filter. Callers store the class and exclude at read time, so a decision about
 * what counts as a visitor can be changed later by a query instead of by a
 * deploy that cannot recover the data it already threw away. The one exception
 * is `first_party`, which is our own servers talking to themselves -- see below.
 *
 * MATCHING IS BY TOKEN, NOT SUBSTRING. The old filters tested `/bot/i` against
 * the raw string, which is unsafe on principle: Cubot is a real Android phone
 * brand, so `CUBOT_NOTE_20` reads as a bot and every one of that brand's owners
 * is filed as a machine. Production shows no such agent today, which is luck
 * rather than design. Generic detection below requires the conventional shape
 * of a bot agent -- `name/version`, `compatible; name`, or the `+http` contact
 * URL -- rather than the three letters appearing anywhere.
 *
 * THE NAMED LIST IS MATCHED FIRST, and on the operator's name rather than on
 * anything incidental. `meta-externalagent/1.1` currently matches the old
 * filters only because the word "crawler" appears in the documentation URL it
 * carries; rename that URL and Meta's crawler silently becomes a person.
 */

/** What kind of client this is. Stored per row; see scripts/clickhouse. */
export const TRAFFIC_CLASS = {
  /** A browser, driven by a person, with no sign of anything else. */
  HUMAN: 'human',
  /** Indexes the site so people can find it. Googlebot, bingbot, Yandex. */
  SEARCH_CRAWLER: 'search_crawler',
  /** Reads the site to train or answer with. GPTBot, ClaudeBot, Perplexity. */
  AI_CRAWLER: 'ai_crawler',
  /** Commercial link and keyword crawlers. Ahrefs, Semrush, Majestic. */
  SEO_CRAWLER: 'seo_crawler',
  /** Fetches one page to render a link preview. Not a crawl -- a person shared a link. */
  SOCIAL_PREVIEW: 'social_preview',
  /** A real browser engine driven by a tool. Headless Chrome, Puppeteer, Selenium. */
  AUTOMATION: 'automation',
  /** A library or CLI, not a browser. curl, axios, python-requests, Go-http-client. */
  HTTP_CLIENT: 'http_client',
  /**
   * A service we do business with, checking our pages.
   *
   * Not search, not AI, not SEO, not a link preview -- Stripe crawls listing
   * pages to verify the commerce data behind its own checkout, and an uptime
   * monitor fetches a page to see whether it answers. Its own class because
   * the right response is different from every other kind: you do not block
   * it, you do not court it, you just need it out of your visitor counts.
   *
   * ON PRODUCTION THIS WAS 10-19% OF DAILY "VISITORS". Stripebot mints a fresh
   * browser id per request from two IP addresses, so two machines read as up
   * to 1,241 unique people in a day, every day, and the old filter never named
   * it so it counted as traffic the whole time.
   */
  SERVICE_CRAWLER: 'service_crawler',
  /**
   * Corporate mail security opening a link before the recipient does.
   *
   * Its own class because the consequence is unique: these fetch links out of
   * emails to check them, so a one-click unsubscribe or a confirm-my-address
   * link gets ACTIONED by a machine on behalf of someone who never clicked it.
   * See pages/api/unsubscribe.ts, which has refused them since before this
   * taxonomy existed and which is where the names came from.
   */
  EMAIL_SCANNER: 'email_scanner',
  /** Our own servers: cron jobs, server-side fetches, the analytics outbox. */
  FIRST_PARTY: 'first_party',
  /** Declares itself a machine but names nobody, or sends no agent at all. */
  UNKNOWN_BOT: 'unknown_bot',
} as const;

export type TrafficClass = (typeof TRAFFIC_CLASS)[keyof typeof TRAFFIC_CLASS];

/** The classes that are a person. Everything else is a machine of some kind. */
export const HUMAN_CLASSES: readonly TrafficClass[] = [TRAFFIC_CLASS.HUMAN];

/**
 * Declared crawlers: they say who they are, publish what they do, and read
 * robots.txt. Tracked, never treated as visitors.
 */
export const CRAWLER_CLASSES: readonly TrafficClass[] = [
  TRAFFIC_CLASS.SEARCH_CRAWLER,
  TRAFFIC_CLASS.AI_CRAWLER,
  TRAFFIC_CLASS.SEO_CRAWLER,
  TRAFFIC_CLASS.SOCIAL_PREVIEW,
  TRAFFIC_CLASS.SERVICE_CRAWLER,
];

/**
 * A row this system wrote about itself.
 *
 * The only class callers refuse to store, because it is not traffic: it is our
 * cron reaching our own API, and counting it would put our infrastructure in
 * every visitor number. Kept as a class rather than as a silent drop so the
 * refusal is a named decision one function owns.
 */
export function isFirstParty(trafficClass: TrafficClass): boolean {
  return trafficClass === TRAFFIC_CLASS.FIRST_PARTY;
}

type Rule = { name: string; test: RegExp; klass: TrafficClass };

/** `name` as a whole token: bounded by a non-alphanumeric or by either end. */
function token(name: string): RegExp {
  const escaped = name.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  return new RegExp(`(^|[^a-z0-9])${escaped}([^a-z0-9]|$)`, 'i');
}

/**
 * `name` at the START of a word, with anything allowed after it.
 *
 * For vendors that glue a product name straight onto the company name --
 * `BarracudaCentral/1.0` is real, and `token('barracuda')` misses it because it
 * demands a boundary that is not there. Still anchored on the LEFT, so this is
 * not a bare substring test and `Cubot` is still a phone.
 */
function prefix(name: string): RegExp {
  const escaped = name.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  return new RegExp(`(^|[^a-z0-9])${escaped}`, 'i');
}

/**
 * Named operators, most specific first.
 *
 * Each entry is one organisation's crawler, matched on the name it publishes.
 * A name that is not here still gets classified -- as `unknown_bot` if it
 * declares itself, as `http_client` if it is a library -- so an unlisted
 * crawler is recorded and visible rather than mistaken for a person.
 */
const NAMED: readonly Rule[] = [
  // --- Our own infrastructure. FIRST, so nothing below can claim it. -------
  // `cron/<job>` is set deliberately by our scheduled jobs; `Node.js vX` and a
  // bare `node` are what Node's own fetch sends when a server-side call does
  // not set one. All three are single-IP on production. The durable fix is an
  // explicit agent on every internal call rather than recognising the default.
  { name: 'cron', test: /^cron\//i, klass: TRAFFIC_CLASS.FIRST_PARTY },
  { name: 'node', test: /^Node\.js\s*v?[\d.]*$/i, klass: TRAFFIC_CLASS.FIRST_PARTY },
  { name: 'node', test: /^node$/i, klass: TRAFFIC_CLASS.FIRST_PARTY },
  { name: 'server', test: /^server$/i, klass: TRAFFIC_CLASS.FIRST_PARTY },

  // --- Search: indexes the site so people can find it ----------------------
  { name: 'googlebot', test: token('googlebot'), klass: TRAFFIC_CLASS.SEARCH_CRAWLER },
  { name: 'bingbot', test: token('bingbot'), klass: TRAFFIC_CLASS.SEARCH_CRAWLER },
  { name: 'yandexbot', test: /yandex[a-z]*bot/i, klass: TRAFFIC_CLASS.SEARCH_CRAWLER },
  { name: 'baiduspider', test: token('baiduspider'), klass: TRAFFIC_CLASS.SEARCH_CRAWLER },
  { name: 'duckduckbot', test: token('duckduckbot'), klass: TRAFFIC_CLASS.SEARCH_CRAWLER },
  { name: 'applebot', test: token('applebot'), klass: TRAFFIC_CLASS.SEARCH_CRAWLER },
  { name: 'slurp', test: token('slurp'), klass: TRAFFIC_CLASS.SEARCH_CRAWLER },
  { name: 'sogou', test: token('sogou'), klass: TRAFFIC_CLASS.SEARCH_CRAWLER },
  { name: 'seznambot', test: token('seznambot'), klass: TRAFFIC_CLASS.SEARCH_CRAWLER },

  // --- AI: reads the site to train on or to answer with --------------------
  // Worth its own class rather than filed under search: the traffic is growing,
  // it converts to nothing, and whether to allow it is a live business
  // question that needs a number behind it.
  { name: 'gptbot', test: token('gptbot'), klass: TRAFFIC_CLASS.AI_CRAWLER },
  { name: 'oai-searchbot', test: token('oai-searchbot'), klass: TRAFFIC_CLASS.AI_CRAWLER },
  { name: 'chatgpt-user', test: token('chatgpt-user'), klass: TRAFFIC_CLASS.AI_CRAWLER },
  { name: 'claudebot', test: token('claudebot'), klass: TRAFFIC_CLASS.AI_CRAWLER },
  { name: 'claude-web', test: token('claude-web'), klass: TRAFFIC_CLASS.AI_CRAWLER },
  { name: 'anthropic-ai', test: token('anthropic-ai'), klass: TRAFFIC_CLASS.AI_CRAWLER },
  { name: 'perplexitybot', test: token('perplexitybot'), klass: TRAFFIC_CLASS.AI_CRAWLER },
  { name: 'ccbot', test: token('ccbot'), klass: TRAFFIC_CLASS.AI_CRAWLER },
  { name: 'bytespider', test: token('bytespider'), klass: TRAFFIC_CLASS.AI_CRAWLER },
  { name: 'google-extended', test: token('google-extended'), klass: TRAFFIC_CLASS.AI_CRAWLER },
  { name: 'meta-externalagent', test: token('meta-externalagent'), klass: TRAFFIC_CLASS.AI_CRAWLER },
  { name: 'applebot-extended', test: token('applebot-extended'), klass: TRAFFIC_CLASS.AI_CRAWLER },
  { name: 'amazonbot', test: token('amazonbot'), klass: TRAFFIC_CLASS.AI_CRAWLER },
  // Amazon Quick's agent fetching a page for one of its users, like
  // ChatGPT-User. Agent `amazon-Quick-on-behalf-of-<hex>`: no "bot" in it, so
  // on prod 2026-10-24..31 it was 162 "human" sessions from one IP, every
  // outside script failing, on the Blocked scripts tab.
  { name: 'amazon-quick', test: token('amazon-quick'), klass: TRAFFIC_CLASS.AI_CRAWLER },
  { name: 'cohere-ai', test: token('cohere-ai'), klass: TRAFFIC_CLASS.AI_CRAWLER },

  // --- SEO and market research --------------------------------------------
  { name: 'ahrefsbot', test: /ahrefs[a-z]*bot/i, klass: TRAFFIC_CLASS.SEO_CRAWLER },
  { name: 'semrushbot', test: /semrush[a-z]*bot/i, klass: TRAFFIC_CLASS.SEO_CRAWLER },
  { name: 'mj12bot', test: token('mj12bot'), klass: TRAFFIC_CLASS.SEO_CRAWLER },
  { name: 'dotbot', test: token('dotbot'), klass: TRAFFIC_CLASS.SEO_CRAWLER },
  { name: 'petalbot', test: token('petalbot'), klass: TRAFFIC_CLASS.SEO_CRAWLER },
  { name: 'rogerbot', test: token('rogerbot'), klass: TRAFFIC_CLASS.SEO_CRAWLER },
  { name: 'blexbot', test: token('blexbot'), klass: TRAFFIC_CLASS.SEO_CRAWLER },
  { name: 'dataforseobot', test: token('dataforseobot'), klass: TRAFFIC_CLASS.SEO_CRAWLER },

  // --- Link previews -------------------------------------------------------
  // NOT a crawl. Somebody pasted a link into a chat and the platform fetched
  // the page once to draw a card, which means a person is about to see it.
  { name: 'facebookexternalhit', test: token('facebookexternalhit'), klass: TRAFFIC_CLASS.SOCIAL_PREVIEW },
  { name: 'twitterbot', test: token('twitterbot'), klass: TRAFFIC_CLASS.SOCIAL_PREVIEW },
  { name: 'linkedinbot', test: token('linkedinbot'), klass: TRAFFIC_CLASS.SOCIAL_PREVIEW },
  { name: 'slackbot', test: /slackbot|slack-imgproxy/i, klass: TRAFFIC_CLASS.SOCIAL_PREVIEW },
  { name: 'discordbot', test: token('discordbot'), klass: TRAFFIC_CLASS.SOCIAL_PREVIEW },
  { name: 'whatsapp', test: token('whatsapp'), klass: TRAFFIC_CLASS.SOCIAL_PREVIEW },
  { name: 'telegrambot', test: token('telegrambot'), klass: TRAFFIC_CLASS.SOCIAL_PREVIEW },
  { name: 'pinterestbot', test: /pinterest(bot|\/)/i, klass: TRAFFIC_CLASS.SOCIAL_PREVIEW },
  { name: 'redditbot', test: token('redditbot'), klass: TRAFFIC_CLASS.SOCIAL_PREVIEW },
  { name: 'embedly', test: token('embedly'), klass: TRAFFIC_CLASS.SOCIAL_PREVIEW },

  // --- Automation: a real browser engine, driven by a tool -----------------
  // These render and run JavaScript, so they look like people to everything
  // except the agent string. The ones that scrape usually remove it.
  { name: 'headlesschrome', test: token('headlesschrome'), klass: TRAFFIC_CLASS.AUTOMATION },
  { name: 'puppeteer', test: token('puppeteer'), klass: TRAFFIC_CLASS.AUTOMATION },
  { name: 'playwright', test: token('playwright'), klass: TRAFFIC_CLASS.AUTOMATION },
  { name: 'selenium', test: token('selenium'), klass: TRAFFIC_CLASS.AUTOMATION },
  { name: 'phantomjs', test: token('phantomjs'), klass: TRAFFIC_CLASS.AUTOMATION },
  // Electron is deliberately absent. It is an application framework -- Slack's
  // desktop client and VS Code are Electron -- so matching it would file a
  // person using a normal app as a machine. Automation means a browser engine
  // being DRIVEN, and Electron alone does not say that.
  { name: 'cypress', test: token('cypress'), klass: TRAFFIC_CLASS.AUTOMATION },

  // --- Services we use, checking our pages --------------------------------
  { name: 'stripebot', test: token('stripebot'), klass: TRAFFIC_CLASS.SERVICE_CRAWLER },
  { name: 'paypal', test: /paypal[- ]?(ipn|bot)/i, klass: TRAFFIC_CLASS.SERVICE_CRAWLER },
  { name: 'shopify', test: token('shopify-captain-hook'), klass: TRAFFIC_CLASS.SERVICE_CRAWLER },
  { name: 'cloudflare', test: /cloudflare-(traffic-manager|healthchecks|alwaysonline)/i, klass: TRAFFIC_CLASS.SERVICE_CRAWLER },
  { name: 'uptime-monitor', test: /pingdom|uptimerobot|statuscake|site24x7|newrelicpinger|datadog/i, klass: TRAFFIC_CLASS.SERVICE_CRAWLER },
  { name: 'google-site-verification', test: token('google-site-verification'), klass: TRAFFIC_CLASS.SERVICE_CRAWLER },
  { name: 'vercel', test: /vercel-(screenshot|favicon|fetch)/i, klass: TRAFFIC_CLASS.SERVICE_CRAWLER },

  // --- Mail security: opens the link before the person does ----------------
  { name: 'proofpoint', test: prefix('proofpoint'), klass: TRAFFIC_CLASS.EMAIL_SCANNER },
  { name: 'barracuda', test: prefix('barracuda'), klass: TRAFFIC_CLASS.EMAIL_SCANNER },
  { name: 'mimecast', test: prefix('mimecast'), klass: TRAFFIC_CLASS.EMAIL_SCANNER },
  { name: 'safelinks', test: /safelinks|outlook-ios|outlook-android/i, klass: TRAFFIC_CLASS.EMAIL_SCANNER },
  { name: 'symantec', test: /symantec|messagelabs/i, klass: TRAFFIC_CLASS.EMAIL_SCANNER },
  { name: 'scanner', test: /(^|[^a-z0-9])(url|link|mail)?scanner([^a-z0-9]|$)/i, klass: TRAFFIC_CLASS.EMAIL_SCANNER },

  // --- HTTP clients: a library or a CLI, never a browser -------------------
  { name: 'curl', test: /^curl\/|[^a-z0-9]curl\//i, klass: TRAFFIC_CLASS.HTTP_CLIENT },
  { name: 'wget', test: token('wget'), klass: TRAFFIC_CLASS.HTTP_CLIENT },
  { name: 'axios', test: /axios\//i, klass: TRAFFIC_CLASS.HTTP_CLIENT },
  { name: 'python-requests', test: /python-requests|python-urllib|aiohttp/i, klass: TRAFFIC_CLASS.HTTP_CLIENT },
  { name: 'go-http-client', test: /go-http-client/i, klass: TRAFFIC_CLASS.HTTP_CLIENT },
  { name: 'java', test: /^java\//i, klass: TRAFFIC_CLASS.HTTP_CLIENT },
  { name: 'okhttp', test: token('okhttp'), klass: TRAFFIC_CLASS.HTTP_CLIENT },
  { name: 'node-fetch', test: token('node-fetch'), klass: TRAFFIC_CLASS.HTTP_CLIENT },
  { name: 'guzzle', test: token('guzzlehttp'), klass: TRAFFIC_CLASS.HTTP_CLIENT },
  { name: 'postman', test: token('postmanruntime'), klass: TRAFFIC_CLASS.HTTP_CLIENT },
  { name: 'scrapy', test: token('scrapy'), klass: TRAFFIC_CLASS.HTTP_CLIENT },
  { name: 'httpclient', test: /apache-httpclient|http_request2|libwww-perl/i, klass: TRAFFIC_CLASS.HTTP_CLIENT },
];

/**
 * Declares itself a machine without naming anybody we know.
 *
 * Deliberately NOT a bare `/bot/i`. A bot agent conventionally carries its name
 * as `name/version`, introduces itself with `compatible;`, or leaves a `+http`
 * contact URL -- so those shapes are what is tested, and `CUBOT_NOTE_20` stays
 * a phone.
 */
const GENERIC_BOT = /(bot|crawler|spider|scraper)[/\s;)\]]|[/\s;(](bot|crawler|spider)\b|\+https?:\/\//i;

/** What a classification says about one request. */
export interface TrafficIdentity {
  trafficClass: TrafficClass;
  /**
   * The operator, when one was named: `googlebot`, `gptbot`, `ahrefsbot`.
   *
   * Empty for a person and for a machine that named nobody. Kept beside the
   * class because "an AI crawler read 40,000 listings" and "ChatGPT read
   * 40,000 listings" are answers to different questions, and only the second
   * one tells you who to talk to.
   */
  operator: string;
}

const HUMAN_IDENTITY: TrafficIdentity = { trafficClass: TRAFFIC_CLASS.HUMAN, operator: '' };

/**
 * Classify one request by its user agent.
 *
 * An EMPTY agent is `unknown_bot`, not `human`: every real browser sends one,
 * so its absence is a machine that chose not to identify itself. It is recorded
 * under that name rather than dropped, because "how much traffic refuses to say
 * what it is" is worth a number.
 */
export function classifyTraffic(userAgent: string | undefined | null): TrafficIdentity {
  const agent = (userAgent || '').trim();
  if (!agent) return { trafficClass: TRAFFIC_CLASS.UNKNOWN_BOT, operator: '' };

  for (const rule of NAMED) {
    if (rule.test.test(agent)) return { trafficClass: rule.klass, operator: rule.name };
  }

  if (GENERIC_BOT.test(agent)) return { trafficClass: TRAFFIC_CLASS.UNKNOWN_BOT, operator: '' };

  return HUMAN_IDENTITY;
}

/** Everything that is not a person, for a caller that only needs the one bit. */
export function isMachine(trafficClass: TrafficClass): boolean {
  return trafficClass !== TRAFFIC_CLASS.HUMAN;
}

/**
 * The same "is this a machine" question, as ClickHouse SQL.
 *
 * ClickHouse cannot call `classifyTraffic`, so a query that needs the answer
 * has to re-express it -- and that re-expression is exactly how this codebase
 * ended up with five private definitions of the word "bot". This builds the
 * predicate FROM the same rule list, so SQL and TypeScript cannot drift: add an
 * operator above and every query that uses this picks it up.
 *
 * WHY NOT JUST READ `traffic_class`. Going forward, queries should -- it is a
 * stored column and it is cheaper. This exists for the rows written BEFORE the
 * column did, which carry `''` and are the only history there is. Callers that
 * want both use `machineFromRowSql`.
 *
 * @param column the user-agent column, e.g. `any(user_agent)`.
 */
export function machineFromUserAgentSql(column: string): string {
  // Only the literal needles: the anchored rules (`^cron/`, `^node$`) describe
  // our own traffic, which no analytics query should be counting anyway, and
  // an anchor does not survive being turned into a substring test.
  const needles = [
    'bot', 'crawl', 'spider', 'slurp', 'headless', 'puppeteer', 'playwright',
    'selenium', 'phantomjs', 'curl/', 'wget', 'axios/', 'python-requests',
    'go-http-client', 'okhttp', 'scrapy', 'proofpoint', 'barracuda', 'mimecast',
  ];
  return `(${needles.map((needle) => `positionCaseInsensitive(${column}, '${needle}') > 0`).join(' OR ')})`;
}

/**
 * The whole taxonomy as one ClickHouse expression, generated from the rules
 * above rather than written out again.
 *
 * WHY THIS EXISTS. `traffic_class` was added as a column and every row written
 * before it carries ''. That is 4.28 million rows going back to 2025-12-01 --
 * and 99.6% of them still carry the user agent they arrived with, so the
 * answer is recoverable. The alternative was telling everyone that history is
 * simply unknowable, which was wrong: the evidence was right there in the
 * column next to it.
 *
 * GENERATED, NOT TRANSCRIBED. Each rule's own `RegExp.source` becomes the
 * pattern, so a name added to `NAMED` is picked up here with no second edit and
 * the backfill cannot drift from the live classifier. `(?i)` is RE2's inline
 * case-insensitive flag, which is what ClickHouse's `match` speaks.
 *
 * WHAT IT CANNOT RECOVER. Rows the old ingest filters DELETED. Googlebot and
 * the fifteen other names on those lists never reached the table, so no
 * expression can find them. What it does recover is everything that was kept
 * and miscounted -- which is the larger and more damaging group, Stripebot
 * among them at 10-19% of daily visitors.
 */
export function trafficClassSql(userAgentColumn: string): string {
  const literal = (pattern: RegExp) =>
    `'(?i)${pattern.source.replace(/\\/g, '\\\\').replace(/'/g, "\\'")}'`;

  const branches = NAMED.map(
    (rule) => `match(${userAgentColumn}, ${literal(rule.test)}), '${rule.klass}'`
  ).join(',\n    ');

  // Same order as `classifyTraffic`: named rules, then the generic shape, then
  // a person. An empty agent is handled first because `match` on '' would fall
  // through to 'human', and the function it mirrors calls that an unknown bot.
  return `multiIf(
    ${userAgentColumn} = '', '${TRAFFIC_CLASS.UNKNOWN_BOT}',
    ${branches},
    match(${userAgentColumn}, ${literal(GENERIC_BOT)}), '${TRAFFIC_CLASS.UNKNOWN_BOT}',
    '${TRAFFIC_CLASS.HUMAN}'
  )`;
}

/** The operator name, by the same rules and in the same order. */
export function botOperatorSql(userAgentColumn: string): string {
  const literal = (pattern: RegExp) =>
    `'(?i)${pattern.source.replace(/\\/g, '\\\\').replace(/'/g, "\\'")}'`;

  const branches = NAMED.map(
    (rule) => `match(${userAgentColumn}, ${literal(rule.test)}), '${rule.name}'`
  ).join(',\n    ');

  return `multiIf(
    ${userAgentColumn} = '', '',
    ${branches},
    ''
  )`;
}

/**
 * A cheap test for "this agent could possibly be a machine".
 *
 * WHY A GATE AT ALL. A ClickHouse mutation rewrites whole parts, so the
 * backfill gets one pass over the data and the cost inside it is the ~60
 * regexes `trafficClassSql` runs per row. Only 4.69% of production rows
 * contain any of these substrings, so gating the regex chain behind one
 * Aho-Corasick scan skips it for the other 95.31%.
 *
 * IT MUST BE A SUPERSET of anything the rules can match. A needle missing here
 * does not merely slow the backfill down -- it silently files a bot as a
 * person, because the gate short-circuits before the regex that would have
 * caught it. The list is therefore wider than it needs to be, and
 * `scripts/clickhouse/verify-traffic-gate.ts` proves it over every distinct
 * agent in production rather than trusting this comment.
 */
export const MACHINE_NEEDLES: readonly string[] = [
  // Anything the generic shape would catch.
  'bot', 'crawl', 'spider', 'scraper', '+http',
  // Named operators whose name contains none of the above.
  'chatgpt', 'claude', 'anthropic', 'google-extended', 'meta-external', 'cohere', 'amazon-quick',
  'facebookexternalhit', 'whatsapp', 'pinterest', 'embedly', 'slack',
  'stripe', 'paypal', 'shopify', 'cloudflare', 'pingdom', 'uptimerobot',
  'statuscake', 'site24x7', 'newrelicpinger', 'datadog', 'google-site-verification',
  'vercel-', 'proofpoint', 'barracuda', 'mimecast', 'safelinks', 'outlook-ios',
  'outlook-android', 'symantec', 'messagelabs', 'scanner',
  'headless', 'puppeteer', 'playwright', 'selenium', 'phantom', 'cypress',
  'curl/', 'wget', 'axios/', 'python-requests', 'python-urllib', 'aiohttp',
  'go-http-client', 'java/', 'okhttp', 'node-fetch', 'guzzle', 'postmanruntime',
  'scrapy', 'apache-httpclient', 'http_request2', 'libwww-perl',
  'cron/', 'node', 'server', 'slurp', 'sogou', 'yandex', 'baidu',
];

/** The gate as SQL. `multiSearchAnyCaseInsensitive` is one Aho-Corasick pass. */
export function machineNeedleSql(userAgentColumn: string): string {
  const needles = MACHINE_NEEDLES.map((n) => `'${n.replace(/'/g, "\\'")}'`).join(', ');
  return `multiSearchAnyCaseInsensitive(${userAgentColumn}, [${needles}])`;
}

/**
 * The backfill expression: the gate, then the full taxonomy only where it fires.
 *
 * An empty agent is `unknown_bot` and is handled inside `trafficClassSql`, so
 * it has to reach that branch rather than be gated away as a person.
 */
export function backfillTrafficClassSql(userAgentColumn: string): string {
  return `if(${userAgentColumn} != '' AND NOT ${machineNeedleSql(userAgentColumn)}, '${TRAFFIC_CLASS.HUMAN}', ${trafficClassSql(userAgentColumn)})`;
}

/** The operator, gated the same way: a person names nobody. */
export function backfillBotOperatorSql(userAgentColumn: string): string {
  return `if(${userAgentColumn} != '' AND NOT ${machineNeedleSql(userAgentColumn)}, '', ${botOperatorSql(userAgentColumn)})`;
}
